// N8N Conversation Analytics Provider
//
// This provider handles conversation-specific event tracking by sending data to N8N webhooks.
// It migrates logic from the original analytics_conversation_n8n.ts file to work with
// the new provider architecture and focuses only on conversation/message events.

import type { NetworkAdapter } from '../../adapters/network';
import { getConsentMode } from '../../services/consentMode';
import { logger } from '../../utils/core/logger';
import { type DeviceType, getDeviceType } from '../../utils/device/mobileDetection';
import type { AnalyticsContext, AnalyticsEvent } from '../types';
import { EventCategories, EventTypes, type MessageSourceType } from '../types';
import { SimpleAnalyticsProvider } from './base';

export interface ConversationConfig {
    conversationUrl: string;
    siteName: string;
    networkAdapter?: NetworkAdapter;
}

interface ConversationData {
    query: string;
    site_web: string;
    full_url: string;
    page_title: string;
    messageId: string;
    conversation_history: Array<{
        role: string;
        content: string;
        timestamp: string;
    }>;
    output: string;
    sources?: Array<{
        title: string;
        url: string;
        snippet: string;
    }>;
    sources_count: number;
    has_sources: boolean;
    threadId?: string;
    timestamp: string;
    sessionId?: string;
    message_source?: MessageSourceType;
    device_type?: DeviceType;
    visitor_profile?: Record<string, unknown>;
}

export class N8nConversationProvider extends SimpleAnalyticsProvider {
    public readonly name = 'n8n-conversation';

    private config: ConversationConfig;
    private readonly privateSessions = new Set<string | undefined>();

    public override async track(event: AnalyticsEvent): Promise<void> {
        if (
            getConsentMode() !== 'full' ||
            (event.consentMode !== undefined && event.consentMode !== 'full')
        ) {
            this.privateSessions.add(event.context.sessionId);
        }
        await super.track(event);
    }

    public override async initialize(context: AnalyticsContext): Promise<void> {
        await super.initialize(context);
        if (getConsentMode() !== 'full') this.privateSessions.add(context.sessionId);
    }

    public override updateContext(context: Partial<AnalyticsContext>): void {
        super.updateContext(context);
        if (getConsentMode() !== 'full') this.privateSessions.add(this.getContext().sessionId);
    }

    constructor(config: ConversationConfig, timeoutMs: number = 2000) {
        // Fire-and-forget: Only wait 2s for connection, not for N8N pipeline completion
        super(timeoutMs);
        this.config = config;
    }

    public eventFilter(event: AnalyticsEvent): boolean {
        // Only handle conversation-related events
        if (event.category === EventCategories.CONVERSATION) {
            return true;
        }

        // Also handle the specific conversation tracking event
        if (event.type === EventTypes.CONVERSATION_TRACKED) {
            return true;
        }

        // Filter out all other events
        return false;
    }

    /**
     * Fire-and-forget (ROSE-FRONT-G3): stop waiting after timeoutMs without failing.
     * The /conversation proxy answers only after the n8n pipeline, and the wait limit does not
     * cancel the request, so a slow delivery usually still succeeds. A failure inside the limit
     * still rejects; a later one is reported by callWebhookViaNetwork and sendEventImpl.
     */
    protected async sendEvent(event: AnalyticsEvent): Promise<void> {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        const waitLimit = new Promise<void>((resolve) => {
            timeoutId = setTimeout(resolve, this.timeoutMs);
        });
        try {
            await Promise.race([this.sendEventImpl(event), waitLimit]);
        } finally {
            clearTimeout(timeoutId);
        }
    }

    protected async sendEventImpl(event: AnalyticsEvent): Promise<void> {
        // Full-mode events can contain the complete chat history: never replay private turns.
        if (this.privateSessions.has(event.context.sessionId)) return;
        if (event.type !== EventTypes.CONVERSATION_TRACKED) {
            logger.debug(`N8N provider ignoring non-conversation event: ${event.type}`);
            return;
        }

        // Convert analytics event to conversation data format
        const conversationData = this.convertToConversationData(event);

        logger.debug(
            'N8nConversationProvider: Tracking conversation for messageId:',
            conversationData.messageId,
            'URL:',
            this.config.conversationUrl,
            'siteName:',
            this.config.siteName,
            'site_web:',
            conversationData.site_web
        );

        try {
            // Fire-and-forget: Send to webhook and accept immediate response
            // We don't wait for N8N pipeline completion (which takes ~7s)
            await this.callWebhookViaNetwork(conversationData);

            logger.debug(
                'N8nConversationProvider: Conversation sent successfully (fire-and-forget) to URL:',
                this.config.conversationUrl
            );
        } catch (error) {
            logger.error(
                'N8nConversationProvider: Network error tracking conversation to URL:',
                this.config.conversationUrl,
                error
            );
            throw error; // Re-throw to let event bus handle the error
        }
    }

    private convertToConversationData(
        event: AnalyticsEvent
    ): ConversationData & { personId?: string } {
        const props = event.properties;
        const context = event.context;

        if (!context.domain) {
            throw new Error('Domain not available in analytics context');
        }

        return {
            query: (props.query as string) || '',
            site_web: context.domain,
            full_url:
                (props.full_url as string) ||
                (typeof window !== 'undefined' ? window.location.href : ''),
            page_title:
                (props.page_title as string) ||
                (typeof document !== 'undefined' ? document.title : ''),
            messageId: (props.messageId as string) || `msg_${Date.now()}`,
            conversation_history:
                (props.conversation_history as ConversationData['conversation_history']) || [],
            output: (props.output as string) || '',
            sources: (props.sources as ConversationData['sources']) || [],
            sources_count: (props.sources_count as number) || 0,
            has_sources: (props.has_sources as boolean) || false,
            threadId: props.threadId as string | undefined,
            timestamp: new Date(event.timestamp).toISOString(),
            sessionId: context.sessionId,
            personId: context.personId, // Include personId for conversation tracking
            message_source: (props.message_source as MessageSourceType) || 'user_typed',
            device_type: getDeviceType(),
            // Pass complete visitor profile as nested object (future-proof)
            // All fields from backend model_dump() are automatically included
            visitor_profile: (props.visitor_profile as Record<string, unknown>) || undefined,
        };
    }

    private async callWebhookViaNetwork(data: ConversationData): Promise<void> {
        // Get proxy endpoint from environment
        const proxyEndpoint = import.meta.env.VITE_CONVERSATION_ENDPOINT;

        if (!proxyEndpoint) {
            // Fallback to direct webhook call if proxy not configured
            logger.warn(
                'N8nConversationProvider: VITE_CONVERSATION_ENDPOINT not configured, using direct webhook'
            );
            return this.directWebhookCall(data);
        }

        // Prepare wrapped payload for proxy
        const proxyPayload = {
            webhook_url: this.config.conversationUrl,
            data: data,
        };

        // Use network adapter if available
        if (this.config.networkAdapter) {
            try {
                await this.config.networkAdapter.post(proxyEndpoint, proxyPayload);
                // Fire-and-forget: Accept any successful response from proxy
                return;
            } catch (error) {
                // logger.error automatically sends to Sentry in production with full context
                logger.error('N8nConversationProvider: Network adapter failed', error, {
                    provider: 'n8n-conversation',
                    endpoint: proxyEndpoint,
                    webhookUrl: this.config.conversationUrl,
                    messageId: data.messageId,
                    threadId: data.threadId,
                    sessionId: data.sessionId,
                    site_web: data.site_web,
                });
                throw error;
            }
        }

        // Fallback to direct fetch through proxy
        try {
            const response = await fetch(proxyEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(proxyPayload),
            });

            // Fire-and-forget: Accept 200, 202 (Accepted), or 204 (No Content) as success
            if (response.ok || response.status === 202) {
                logger.debug(
                    `N8nConversationProvider: Request accepted with status ${response.status}`
                );
                return;
            }

            throw new Error(`HTTP error! status: ${response.status}`);
        } catch (error) {
            // logger.error automatically sends to Sentry in production with full context
            logger.error('N8nConversationProvider: Proxy request failed', error, {
                provider: 'n8n-conversation',
                endpoint: proxyEndpoint,
                webhookUrl: this.config.conversationUrl,
                messageId: data.messageId,
                threadId: data.threadId,
                sessionId: data.sessionId,
                site_web: data.site_web,
            });
            throw error;
        }
    }

    /**
     * Direct webhook call (fallback when proxy not configured)
     */
    private async directWebhookCall(data: ConversationData): Promise<void> {
        try {
            const response = await fetch(this.config.conversationUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            // Fire-and-forget: Accept 200, 202 (Accepted), or 204 (No Content) as success
            if (response.ok || response.status === 202) {
                logger.debug(
                    `N8nConversationProvider: Direct webhook accepted with status ${response.status}`
                );
                return;
            }

            throw new Error(`HTTP error! status: ${response.status}`);
        } catch (error) {
            logger.error('N8nConversationProvider: Direct fetch failed', error, {
                provider: 'n8n-conversation',
                endpoint: this.config.conversationUrl,
                messageId: data.messageId,
            });
            throw error;
        }
    }

    public updateConfig(newConfig: Partial<ConversationConfig>): void {
        this.config = { ...this.config, ...newConfig };
        logger.debug('N8nConversationProvider: Config updated:', this.config);
    }

    public getConfig(): ConversationConfig {
        return { ...this.config };
    }

    /**
     * Helper method to track a conversation (maintains compatibility with original API)
     */
    public async trackConversation(conversationData: Partial<ConversationData>): Promise<boolean> {
        try {
            // Convert conversation data to analytics event format
            const event: AnalyticsEvent = {
                type: EventTypes.CONVERSATION_TRACKED,
                properties: conversationData,
                context: this.getContext(),
                timestamp: Date.now(),
                category: EventCategories.CONVERSATION,
            };

            await this.track(event);
            return true;
        } catch (error) {
            logger.error('N8nConversationProvider: Error in trackConversation:', error);
            return false;
        }
    }
}
